import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminLogin, listPaymentLinks } from '../lib/api';
import { PaymentLinkForm } from '../components/admin/PaymentLinkForm';
import { PaymentLinkRow } from '../components/admin/PaymentLinkRow';
import { usePageMeta } from '../hooks/use-page-meta';

const KEY_STORAGE = 'elina_admin_key';

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(password);
      onLogin(password);
    } catch (err) {
      toast.error('Wrong password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="contact-form max-w-sm mx-auto" data-testid="admin-login-form">
      <div className="form-group">
        <label className="form-label">Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="form-input" autoFocus data-testid="admin-password-input" />
      </div>
      <button type="submit" disabled={busy} className="btn-submit" data-testid="admin-login-btn">{busy ? 'Checking…' : 'Enter'}</button>
    </form>
  );
};

export const AdminPage = () => {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [links, setLinks] = useState([]);
  usePageMeta({ title: 'Payments', description: 'Private payments page.', path: '/admin' });

  useEffect(() => {
    if (!adminKey) return;
    listPaymentLinks(adminKey).then(setLinks).catch(() => {
      sessionStorage.removeItem(KEY_STORAGE);
      setAdminKey('');
    });
  }, [adminKey]);

  const handleLogin = (key) => {
    sessionStorage.setItem(KEY_STORAGE, key);
    setAdminKey(key);
  };

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAdminKey('');
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a]" style={{ paddingTop: '100px' }} data-testid="admin-page">
      <div className="revolt-container section-spacing">
        <div className="text-center mb-lg">
          <h1 className="section-title">Payments</h1>
          <p className="subtitle">Create a payment link and send it to your client</p>
        </div>

        {!adminKey ? (
          <AdminLogin onLogin={handleLogin} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <PaymentLinkForm adminKey={adminKey} onCreated={(link) => setLinks(prev => [link, ...prev])} />
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}>Payment links</h2>
                <button onClick={logout} className="text-xs tracking-widest uppercase text-[#888] hover:text-white" data-testid="admin-logout-btn">Log out</button>
              </div>
              {links.length === 0 ? (
                <p className="text-[#888] text-sm" data-testid="payment-links-empty">No payment links yet.</p>
              ) : (
                <div className="space-y-4" data-testid="payment-links-list">
                  {links.map(link => <PaymentLinkRow key={link.id} link={link} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
